import { type ReactElement, useState } from 'react';
import { IconButton, Text } from '@vello/web-theme';
import { grantsAllow } from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { usePhoneText } from './phone-text';

/**
 * A masked phone with an eye "Mostrar telefone" action (owner, 2026-10-13, an eye icon since 2026-10-18; ADR-0054 keeps contacts masked off the account
 * record). Offered to an operator holding `account:manage` at country scope (or while the grants are still unknown — the
 * server decides); the click calls `POST /v1/ops/users/:id/contact-reveal`, which writes the reveal to the account's
 * append-only audit BEFORE answering. The whole number lives only in this component's state (never cached, never in the
 * URL) and is gone when the screen unmounts; the crossed eye hides it again without a second audited call. Without
 * `userId` (no account to reveal) the masked value is all there is. A grid that only carries the last digits passes
 * `last4` instead of `masked`.
 */
export function RevealContact({ userId, masked: maskedIn, last4 }: { readonly userId: string | undefined; readonly masked?: string | undefined; readonly last4?: string | null | undefined }): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t } = useT();
  const phoneOf = usePhoneText();
  const [phone, setPhone] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [shown, setShown] = useState(false);
  const masked = maskedIn ?? (last4 != null ? `•••• ${last4}` : undefined);
  const may = userId !== undefined && (grants === undefined || grantsAllow(grants, { permission: 'account:manage', target: 'COUNTRY' }));

  if (phone !== undefined && shown) {
    return (
      <span className="vello-reveal">
        <span className="tnum">{phoneOf(phone)}</span>
        <IconButton icon="eyeOff" label={t('contact.hide')} size={14} onClick={() => setShown(false)} />
      </span>
    );
  }
  const reveal = async (): Promise<void> => {
    if (phone !== undefined) {
      setShown(true);
      return;
    }
    if (userId === undefined) return;
    setBusy(true);
    setFailed(false);
    const res = await runtime.adminUsers.revealContact(userId);
    setBusy(false);
    if (res.ok && res.value.phoneE164 !== undefined) {
      setPhone(res.value.phoneE164);
      setShown(true);
    } else setFailed(true);
  };
  return (
    <span className="vello-reveal">
      <span className="tnum">{masked !== undefined ? phoneOf(masked) : '—'}</span>
      {may && masked !== undefined && (
        <IconButton icon="eye" label={t('contact.reveal')} title={`${t(busy ? 'contact.reveal.busy' : 'contact.reveal')} — ${t('contact.reveal.note')}`} size={14} disabled={busy} aria-busy={busy} onClick={() => void reveal()} />
      )}
      {failed && (
        <Text as="span" role="caption" tone="danger">
          {t('contact.reveal.failed')}
        </Text>
      )}
    </span>
  );
}
